import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import InputTextField from '../../components/TextField';
import { editProfileData, fetchUserById, resetUserUpdateSuccess } from '../../slices/user.slice';
import { RootState } from '../../store';
import { useAppDispatch } from '../../store/hooks';
import { useSelector } from 'react-redux';
import styles from "./Profile.module.scss"
import { pattern } from '../../consts/validationPatterns';

export const UserProfileForm = () => {
  const dispatch = useAppDispatch();
  const { userId } = useSelector((state: RootState) => state.auth);
  const { userProfile, userUpdateSuccess } = useSelector((state: RootState) => state.user);
  const [userProfileData, setUserProfileData] = useState({
    name: '',
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (userId || userUpdateSuccess) {
      dispatch(fetchUserById());
      dispatch(resetUserUpdateSuccess());
    }
  }, [userId, dispatch, userUpdateSuccess]);
  useEffect(() => {
    if (userProfile) {
      setUserProfileData(prev => ({
        ...prev,
        name: userProfile.name,
        email: userProfile.email
      }));
    }
  }, [userProfile]);
  useEffect(() => {
    if (userUpdateSuccess) {
      setUserProfileData(prev => ({
        ...prev,
        newPassword: '',
        confirmPassword: ''
      }));
    }
  }, [userUpdateSuccess]);

  const [toggleIcon] = useState({
    passwordIcon: false,
    confirmPasswordIcon: false,
  });

  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'name':
        if (!value) return 'First name is required';
        if (value.length > 50) return 'First name cannot exceed 50 characters';
        if (value.length < 3) return 'First name must contain min 3 characters';
        return '';

      case 'newPassword':
        if (value && !pattern.password.test(value)) return 'Enter a valid password';
        return '';

      case 'confirmPassword':
        if (value !== userProfileData.newPassword) return 'Passwords do not match';
        return '';

      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(name, e.target)
    setUserProfileData(prev => ({ ...prev, [name]: value }));

    const errorMessage = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: errorMessage }));

    if (name === 'newPassword') {
      const confirmError = userProfileData.confirmPassword
        ? validateField('confirmPassword', userProfileData.confirmPassword)
        : '';
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let isValid = true;
    const newErrors = { ...errors };

    Object.keys(userProfileData).forEach(key => {
      if (key === 'email') return;
      if (key === 'newPassword' && !userProfileData[key]) return;
      if (key === 'confirmPassword' && !userProfileData.newPassword) return;

      const error = validateField(key as keyof typeof errors, userProfileData[key as keyof typeof userProfileData]);
      newErrors[key as keyof typeof errors] = error;
      if (error) isValid = false;
    });

    setErrors(newErrors);

    if (isValid) {
      const finalData = {
        userId: Number(userId),
        name: userProfileData.name,
        password: userProfileData.newPassword,
      };
      console.log('Submitting data:', finalData);
      dispatch(editProfileData(finalData));
    }
  };

  const isFormValid = (): boolean => {
    return (
      !!userProfileData.name &&
      !errors.name &&
      !errors.newPassword &&
      !errors.confirmPassword
    );
  };

  const isFormDirty = (): boolean => {
    return (
      userProfileData.name !== userProfile?.name ||
      !!userProfileData.newPassword
    );
  };
  const handleCancel = () => {
    setUserProfileData({
      name: userProfile?.name || '',
      email: userProfile?.email || '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({
      name: '',
      email: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  return (
    <div className={styles.containerView}>
      <div className={styles.formView}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", padding: "10px 0px" }}>
          <Typography
            variant="h5"
            sx={{
              margin: 0,
              color: "#334155",
              fontFamily: "Poppins, sans-serif",
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
             User Profile
          </Typography>
        </Box>
        <div className={styles.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InputTextField
                label="Name"
                name="name"
                value={userProfileData.name}
                onChange={handleChange}
                className={styles.inputText}
                placeholder="Please enter name"
                type="text"
                error={!!errors.name}
                helperText={errors.name || null}
              />
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <InputTextField
                label="Email"
                name="email"
                value={userProfileData.email}
                className={styles.inputText}
                placeholder="Please enter an email id."
                type="text"
                isDisabled={true}
              />
            </Grid>
          </Grid>
          <Box className='toggle-password-wrapper'>
            <Typography textAlign='left' sx={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              Change Password
            </Typography>
            <Grid container item spacing={2} flexDirection='column'>
              <Grid item xs={12} md={4} sm={6}>
                <InputTextField
                  label="Enter New Password"
                  name="newPassword"
                  value={userProfileData.newPassword}
                  onChange={handleChange}
                  className={styles.inputText}
                  type={toggleIcon.passwordIcon ? 'text' : 'password'}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword || null}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <InputTextField
                  label="Confirm Password"
                  name="confirmPassword"
                  value={userProfileData.confirmPassword}
                  onChange={handleChange}
                  className={styles.inputText}
                  type={toggleIcon.confirmPasswordIcon ? 'text' : 'password'}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword || null}
                />
              </Grid>
            </Grid>
          </Box>
        </div>
        <div className={styles.buttonContainer}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button className={styles.saveButton} disabled={!isFormDirty() || !isFormValid()} onClick={handleSubmit}>
            Save
          </button>
        </div>

      </div>
    </div>
  );
}

export default UserProfileForm;